import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { useToast } from '../../../lib/toast';
import type { CreatorMe, Message } from '../../../lib/types';
import styles from './Hub.module.css';

const MAX_MESSAGE_LEN = 5000;
const POLL_MS = 15_000;

function formatStamp(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

function tempId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function setMessages(
  qc: ReturnType<typeof useQueryClient>,
  fn: (msgs: Message[]) => Message[],
) {
  qc.setQueryData<CreatorMe>(['creator', 'me'], (old) =>
    old ? { ...old, messages: fn(old.messages ?? []) } : old,
  );
}

export function Thread() {
  const qc = useQueryClient();
  const { data } = useCreatorMe();
  const threadRef = useRef<HTMLDivElement | null>(null);
  const messages: Message[] = data?.messages ?? [];

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Poll for incoming admin replies while the thread is visible. The cancelQueries
  // call in onMutate prevents a poll-driven refetch from clobbering an optimistic
  // bubble that hasn't been confirmed yet.
  useEffect(() => {
    const id = window.setInterval(() => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [qc]);

  function retry(failed: Message) {
    setMessages(qc, (msgs) => msgs.filter((m) => m.id !== failed.id));
    sendMessage(qc, failed.text);
  }

  return (
    <div className={styles.vsThread} ref={threadRef}>
      {messages.length === 0 ? (
        <div className={styles.vsThreadEmpty}>
          No messages yet. Send a note below and the Kick Health team will reply here.
        </div>
      ) : (
        messages.map((m) => <Bubble key={m.id} m={m} onRetry={retry} />)
      )}
    </div>
  );
}

// sendMessage drives the optimistic-update flow imperatively so it can be
// invoked from the retry button in Thread as well as from Composer's mutation.
function sendMessage(qc: ReturnType<typeof useQueryClient>, text: string): Promise<void> {
  const tmp = tempId();
  const optimistic: Message = {
    id: tmp,
    from: 'creator',
    senderName: 'You',
    text,
    sentAt: new Date().toISOString(),
    pending: true,
  };
  qc.cancelQueries({ queryKey: ['creator', 'me'] });
  setMessages(qc, (msgs) => [...msgs, optimistic]);
  return api
    .post<{ message: Message }>('/api/creator/messages', { text })
    .then((res) => {
      setMessages(qc, (msgs) => msgs.map((m) => (m.id === tmp ? res.message : m)));
    })
    .catch(() => {
      setMessages(qc, (msgs) =>
        msgs.map((m) => (m.id === tmp ? { ...m, pending: false, failed: true } : m)),
      );
      throw new Error('failed');
    })
    .finally(() => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
    });
}

export function Composer() {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');

  const send = useMutation<unknown, ApiError, { text: string }>({
    mutationFn: async ({ text }) => sendMessage(qc, text),
    onSuccess: () => toast('Message sent'),
    onError: (err) => toast(err.message || 'Message failed to send'),
  });

  function submit() {
    const t = text.trim();
    if (!t || send.isPending) return;
    setText('');
    send.mutate({ text: t });
  }

  return (
    <div className={styles.vsCompose}>
      <textarea
        className={styles.vsComposeInput}
        rows={2}
        value={text}
        maxLength={MAX_MESSAGE_LEN}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a message…"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />
      <button
        type="button"
        className={styles.btnPrimary}
        disabled={!text.trim() || send.isPending}
        onClick={submit}
      >
        {send.isPending ? 'Sending…' : 'Send'}
      </button>
    </div>
  );
}

function Bubble({ m, onRetry }: { m: Message; onRetry: (m: Message) => void }) {
  const isAdmin = m.from === 'admin';
  return (
    <div
      className={`${styles.vsMsgRow} ${isAdmin ? styles.vsMsgAdmin : styles.vsMsgCreator} ${m.pending ? styles.vsMsgPending : ''} ${m.failed ? styles.vsMsgFailed : ''}`}
    >
      <div
        className={`${styles.vsMsgAvatar} ${isAdmin ? styles.vsAvatarAdmin : styles.vsAvatarCreator}`}
      >
        {(m.senderName || '?')[0].toUpperCase()}
      </div>
      <div className={styles.vsMsgBody}>
        <div className={styles.vsMsgMeta}>
          <span className={styles.vsMsgSender}>{m.senderName || ''}</span>
          <span className={styles.vsMsgTime}>
            {m.pending ? 'Sending…' : m.failed ? 'Failed to send' : formatStamp(m.sentAt)}
          </span>
        </div>
        <div
          className={`${styles.vsMsgBubble} ${isAdmin ? styles.vsBubbleAdmin : styles.vsBubbleCreator}`}
        >
          {m.text}
        </div>
        {m.failed && (
          <button type="button" className={styles.vsMsgRetry} onClick={() => onRetry(m)}>
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export function MessagesView() {
  return (
    <>
      <div className={styles.pageActionRow} style={{ marginBottom: 20 }}>
        <div>
          <div className={styles.pageTitle}>Messages</div>
          <div className={styles.pageSubtitle}>Your conversation with the Kick Health team.</div>
        </div>
      </div>
      <div className={styles.card}>
        <Thread />
        <Composer />
      </div>
    </>
  );
}
